import { Directive, ElementRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { E, F, io } from '@macs-frontend/core/utils/fp-ts';
import { rx } from '@macs-frontend/core/utils/rx';
import { isObservable, Observable } from 'rxjs';
import { Instance } from 'tippy.js';

interface TippyHTMLElement extends HTMLElement {
  _tippy: Instance;
}

/**
 * Update tippy by trigger (mobx expression or rxjs observable).
 */
@Directive({
  /* eslint-disable-next-line @angular-eslint/directive-selector */
  selector: '[ngxTippy][mdTippyUpdate]',
})
export class TippyUpdateDirective extends BaseComponent implements OnChanges {
  @Input()
  mdTippyUpdate?: io.IO<unknown> | Observable<unknown>;
  @Input()
  mdTippyUpdateDelay = 1;

  constructor(private readonly el: ElementRef) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('mdTippyUpdate' in changes && '_tippy' in this.el.nativeElement) {
      this.destroy$.next();
      this.disposers.forEach(disposer => disposer());

      const tippy = (this.el.nativeElement as TippyHTMLElement)._tippy;

      const subscribe = F.pipe(
        isObservable(this.mdTippyUpdate)
          ? E.right(this.mdTippyUpdate as Observable<unknown>)
          : E.left(this.mdTippyUpdate as io.IO<unknown>),
        E.fold(
          (data): io.IO<void> =>
            () => {
              this.reaction(
                data,
                () => {
                  tippy.popperInstance?.update();
                },
                { delay: this.mdTippyUpdateDelay },
              );
            },
          (data$): io.IO<void> =>
            () => {
              data$
                .pipe(this.takeUntilDestroy(), rx.debounceTime(this.mdTippyUpdateDelay))
                .subscribe(() => {
                  tippy.popperInstance?.update();
                });
            },
        ),
      );

      subscribe();
    }
  }
}
